import {
    DragDimensionValue,
    DimensionPanelDefaultAction,
    MoveAttribute,
    ToggleDimensionValue,
    ToggleAttributeLabel,
    ReceivedUpdates,
    ToggleMetric,
    ToggleSwitch,
    MoveMetric,
    ToggleAssortmentTotals,
    TogglePlanTotals,
    SetShowDoors,
} from "actions/tool-panel/draft/types";
import { PayloadParam } from "actions/utilities/payload-param-type";
import { Order } from "helpers/door-collection";
import { reorderDraggedItemInArray } from "helpers/dragndrop";
import { setIn } from "helpers/objUtils";
import { Attribute, DimensionsPanelReduxState, DimensionValue } from "../dimensions-panel";
import { mergeAllocationDimensionValuesToState, mergeAttributesToState } from "../helpers";

export const setShowDoorsHandler = (
    state: DimensionsPanelReduxState,
    { payload: { showDoors } }: PayloadParam<SetShowDoors>
) => {
    return { ...state, showDoors };
};

export const dragValueHandler = (
    state: DimensionsPanelReduxState,
    { payload }: PayloadParam<DragDimensionValue>
): DimensionsPanelReduxState => {
    const { dimensionIndex, dragIndex, hoverIndex, selectionKind } = payload;

    const dimensionToUpdate = state[selectionKind][dimensionIndex];

    return setIn(state, [selectionKind, dimensionIndex], {
        ...dimensionToUpdate,
        sort: Order.Custom,
        values: reorderDraggedItemInArray(dragIndex, hoverIndex, dimensionToUpdate.values),
    });
};

export const moveAttributeHandler = (
    state: DimensionsPanelReduxState,
    { payload }: PayloadParam<MoveAttribute>
): DimensionsPanelReduxState => {
    const { dragIndex, hoverIndex } = payload;

    return setIn(state, "attributes", reorderDraggedItemInArray(dragIndex, hoverIndex, state.attributes));
};

export const toggleAttributeLabelHandler = (
    state: DimensionsPanelReduxState,
    { payload }: PayloadParam<ToggleAttributeLabel>
): DimensionsPanelReduxState => {
    const { attributeIndex } = payload;

    const attributeToUpdate = state.attributes[attributeIndex];
    const newAttributeValue: Attribute = {
        ...attributeToUpdate,
        showLabelInHeader: !attributeToUpdate.showLabelInHeader,
    };

    return setIn(state, ["attributes", attributeIndex], newAttributeValue);
};

export const selectAllValuesHandler = (
    state: DimensionsPanelReduxState,
    { payload }: PayloadParam<DimensionPanelDefaultAction>
): DimensionsPanelReduxState => {
    const { dimensionIndex, selectionKind } = payload;

    const dimensionToUpdate = state[selectionKind][dimensionIndex];

    const isIndeterminate = dimensionToUpdate.selected && dimensionToUpdate.values.some((value) => !value.selected);

    return setIn(state, [selectionKind, dimensionIndex], {
        ...dimensionToUpdate,
        selected: isIndeterminate ? true : !dimensionToUpdate.selected,
        values: dimensionToUpdate.values.map((value) => ({
            ...value,
            selected: isIndeterminate ? true : !value.selected,
        })),
    });
};

export const toggleValueHandler = (
    state: DimensionsPanelReduxState,
    { payload }: PayloadParam<ToggleDimensionValue>
): DimensionsPanelReduxState => {
    const { dimensionIndex, selectionKind, index, checked } = payload;

    const dimensionToUpdate = state[selectionKind][dimensionIndex];

    const newValuesList = [...dimensionToUpdate.values];

    const newDimensionValue: DimensionValue = {
        ...dimensionToUpdate.values[index],
        selected: checked,
    };

    newValuesList[index] = newDimensionValue;

    return setIn(state, [selectionKind, dimensionIndex], {
        ...dimensionToUpdate,
        selected: checked ? true : newValuesList.some((value) => value.selected),
        values: newValuesList,
    });
};

// //TODO: HANDLE SORTING WHEN ORDERING IS CUSTOM
export const sortAscendingHandler = (
    state: DimensionsPanelReduxState,
    { payload }: PayloadParam<DimensionPanelDefaultAction>
): DimensionsPanelReduxState => {
    const { dimensionIndex, selectionKind } = payload;

    const dimensionToUpdate = state[selectionKind][dimensionIndex];

    return dimensionToUpdate.sort === Order.Ascending // if allready is ascending we bail
        ? state
        : setIn(state, [selectionKind, dimensionIndex], {
              ...dimensionToUpdate,
              sort: Order.Ascending,
              values: [...dimensionToUpdate.values].reverse(),
          });
};

export const sortDescendingHandler = (
    state: DimensionsPanelReduxState,
    { payload }: PayloadParam<DimensionPanelDefaultAction>
): DimensionsPanelReduxState => {
    const { dimensionIndex, selectionKind } = payload;

    const dimensionToUpdate = state[selectionKind][dimensionIndex];

    return dimensionToUpdate.sort === Order.Descending // if allready is descending we bail
        ? state
        : setIn(state, [selectionKind, dimensionIndex], {
              ...dimensionToUpdate,
              sort: Order.Descending,
              values: [...dimensionToUpdate.values].reverse(),
          });
};

export const syncAllocationDimensionUpdates = (
    state: DimensionsPanelReduxState,
    { payload }: PayloadParam<ReceivedUpdates>
): DimensionsPanelReduxState => {
    const {
        dimensionId,
        data: { dimension_values, field_map },
    } = payload;

    const {
        map: {
            fields: {
                attributes: {
                    struct_value: { keys: attributes },
                },
            },
        },
    } = field_map;

    const {
        list: { values },
    } = dimension_values;

    // parsing the values to be acceptable by the build dimensions function
    const dimensionValues = values.map((val) => val.struct_value);

    const targetDimensionIndex = state.dimensions.findIndex((dimension) => dimension.id === dimensionId);

    // early aborting if no allocation with target id was found
    if (targetDimensionIndex === -1) {
        return { ...state };
    }

    // updating dimensions
    let updatedStateWithDimensions = mergeAllocationDimensionValuesToState(state, dimensionId, dimensionValues);

    // updating attributes
    updatedStateWithDimensions = mergeAttributesToState(updatedStateWithDimensions, attributes);

    return updatedStateWithDimensions;
};

export const toggleMetricHandler = (
    state: DimensionsPanelReduxState,
    { payload: { index } }: PayloadParam<ToggleMetric>
) => {
    const metric = state.metrics.values[index];
    const { showValue, showSubtotals, showTotals } = metric;
    if (showValue || showSubtotals || showTotals) {
        return setIn(state, ["metrics", "values", index], {
            ...metric,
            showValue: false,
            showSubtotals: false,
            showTotals: false,
        });
    }

    return setIn(state, ["metrics", "values", index], {
        ...metric,
        showValue: true,
        showSubtotals: true,
        showTotals: true,
    });
};

export const togglerMetricSwitchHandler = (
    state: DimensionsPanelReduxState,
    { payload: { index, toggle } }: PayloadParam<ToggleSwitch>
) => {
    const newValue = !state.metrics.values[index][toggle];
    return setIn(state, ["metrics", "values", index, toggle], newValue);
};

export const moveMetricHandler = (
    state: DimensionsPanelReduxState,
    { payload: { dragIndex, hoverIndex } }: PayloadParam<MoveMetric>
) => {
    const metrics = [...state.metrics.values];
    const [dragMetric] = metrics.splice(dragIndex, 1);
    metrics.splice(hoverIndex, 0, dragMetric);
    return {
        ...state,
        metrics: {
            ...state.metrics,
            values: metrics,
        },
    };
};

export const toggleAssortmentTotalsHandler = (
    state: DimensionsPanelReduxState,
    { payload: { showAssortmentTotals } }: PayloadParam<ToggleAssortmentTotals>
) => {
    if (showAssortmentTotals) {
        state = setIn(state, ["metrics", "showAssortmentPlan"], false);
    }
    return setIn(state, ["metrics", "showAssortmentTotals"], showAssortmentTotals);
};

export const togglePlanTotalsHandler = (
    state: DimensionsPanelReduxState,
    { payload: { showPlanTotals } }: PayloadParam<TogglePlanTotals>
) => {
    if (showPlanTotals) {
        state = setIn(state, ["metrics", "showAssortmentTotals"], false);
    }
    return setIn(state, ["metrics", "showAssortmentPlan"], showPlanTotals);
};
